import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SseMessage, SseService } from '../../../core/sse';
import { MarketViewMessage } from './market-view.model';
import { BaseService } from '../../../services/base.service';

/**
 * The first event on any SSE connection is a handshake ack (e.g. {"type":"ack","data":{"succeeded":true}}),
 * not a market-view payload. Letting it through would emit a "live" tick whose every price/volume
 * field is `undefined`, blanking the depth ladder and the price figures for one frame before the
 * first real tick arrives. `ci` (contract id) is carried by every genuine message, in every
 * protocol version, so it doubles as the payload guard.
 */
function isMarketViewMessage(message: SseMessage<MarketViewMessage>): boolean {
	return message.type !== 'ack' && typeof message.data?.ci === 'number';
}

@Service({ autoProvided: false })
export class MarketViewService extends BaseService {
	private readonly sseService = inject(SseService);
	private readonly destroyRef = inject(DestroyRef);

	public connectAllDeposits(): Observable<MarketViewMessage> {
		const url = new URL('api/v1/subscribe', this.sseApiPath);
		url.searchParams.append('topics', 'MarketViewMessage');
		url.searchParams.append('markets', '3');

		return this.subscribe(url);
	}

	public connectSingleContract(contractId: number): Observable<MarketViewMessage> {
		const url = new URL('api/v1/subscribe', this.sseApiPath);
		url.searchParams.append('topics', 'MarketViewMessage');
		url.searchParams.append('contractIds', contractId.toString());

		return this.subscribe(url);
	}

	private subscribe(url: URL): Observable<MarketViewMessage> {
		return this.sseService.connect<MarketViewMessage>(url.toString()).pipe(
			takeUntilDestroyed(this.destroyRef),
			filter(isMarketViewMessage),
			map(message => message.data)
		);
	}
}
